import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { displayToast } from "../../utils/Toast";
import {
  FaEye,
  FaEyeSlash,
  FaArrowLeft,
  FaUser,
  FaLock,
  FaLayerGroup,
} from "react-icons/fa";
import ErpLogin from "../../assets/svgIcons/Erp_Login.svg";
import { login, forgotPassword } from "../../apis/auth";
import "../../styles/pages/userAuth/Login.css";

const Login = ({ onLogin }) => {
  const navigate = useNavigate();

  const passwordRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const submitButtonRef = useRef(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  const [forgotUsername, setForgotUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [forgotPasswordError, setForgotPasswordError] = useState("");
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState("");

  const handleUsernameKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      passwordRef.current?.focus();
    }
  };

  const handlePasswordKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      submitButtonRef.current?.click();
    }
  };

  const handleKeyDown = (e, nextRef, shouldSubmit = false) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (shouldSubmit && submitButtonRef?.current) {
        submitButtonRef.current.click();
      } else if (nextRef?.current) {
        nextRef.current.focus();
      }
    }
  };

  const validateForm = () => {
    if (username.trim() === "") {
      setLoginError("Username is required");
      return false;
    }

    if (password.trim() === "") {
      setLoginError("Please enter a password");
      return false;
    }

    if (password.trim().length < 8) {
      setLoginError("Password must be at least 8 characters");
      return false;
    }

    setLoginError("");
    return true;
  };

  const validateForgotPasswordForm = () => {
    if (forgotUsername.trim() === "") {
      setForgotPasswordError("Username is required");
      return false;
    }

    if (newPassword.trim() === "") {
      setForgotPasswordError("New password is required");
      return false;
    }

    if (newPassword.trim().length < 8) {
      setForgotPasswordError("New password must be at least 8 characters");
      return false;
    }

    if (newPassword !== confirmPassword) {
      setForgotPasswordError("Passwords do not match");
      return false;
    }

    setForgotPasswordError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }
    setIsLoading(true);
    const loginData = {
      user_name: username,
      password,
    };
    try {
      const data = await login(loginData);
      if (data.description) {
        displayToast(data.description, "error");
      } else if (data.token) {
        localStorage.setItem(
          "sidebarItems",
          JSON.stringify(data.role.user_menu_controls),
        );
        localStorage.setItem("organisation", JSON.stringify(data.organisation));
        localStorage.setItem("user_name", data.user_name || username);
        if (data.user_id) {
          localStorage.setItem("user_id", String(data.user_id));
        }
        onLogin();
        localStorage.setItem("auth_token", data.token);
        localStorage.setItem("role_id", data.role.id);
        localStorage.setItem("role_name", data.role.name || "");
        navigate("/");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!validateForgotPasswordForm()) {
      return;
    }

    setIsLoading(true);
    const forgotPasswordData = {
      user_name: forgotUsername,
      password: newPassword,
    };

    try {
      const data = await forgotPassword(forgotPasswordData);
      if (data.error || data.description || data.message) {
        setForgotPasswordError(data.error || data.description || data.message);
        displayToast(data.error || data.description || data.message, "error");
      } else {
        setForgotPasswordSuccess("Password has been reset successfully");
        displayToast("Password has been reset successfully", "success");
        setTimeout(() => {
          setShowForgotPassword(false);
          clearForgotPasswordFields();
        }, 2000);
      }
    } catch (error) {
      setForgotPasswordError("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const clearForgotPasswordFields = () => {
    setForgotUsername("");
    setNewPassword("");
    setConfirmPassword("");
    setForgotPasswordError("");
    setForgotPasswordSuccess("");
  };

  const toggleForgotPassword = (e) => {
    e.preventDefault();
    setShowForgotPassword(!showForgotPassword);
    clearForgotPasswordFields();
  };

  return (
    <div className="login-page">
      <div className="login-content">
        <div className="login-image">
          <h1>Simplify Complexity, Amplify Growth</h1>
          <img src={ErpLogin} alt="Login" className="loginImage" />
        </div>
        <div className="login-form-container">
          {!showForgotPassword ? (
            <div className="login-form">
              <div className="login-brand">
                <span className="login-brand-badge">
                  <FaLayerGroup />
                </span>
              </div>
              <h2>ERP</h2>
              <p>Login your account</p>
              <form onSubmit={handleSubmit}>
                <div className="login-field">
                  <FaUser className="field-icon" />
                  <input
                    type="text"
                    placeholder="user name"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onKeyDown={handleUsernameKeyDown}
                  />
                </div>
                <div className="password-container">
                  <FaLock className="field-icon" />
                  <input
                    ref={passwordRef}
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handlePasswordKeyDown}
                  />
                  <div
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <FaEye /> : <FaEyeSlash />}
                  </div>
                </div>
                <a
                  href="#"
                  className="forgot-password"
                  onClick={toggleForgotPassword}
                >
                  Forgot Password?
                </a>
                <div className="login-error-message-container">
                  {loginError && (
                    <div className="login-error-message">{loginError}</div>
                  )}
                </div>
                {isLoading ? (
                  <div className="loader-container">
                    <span className="loader"></span>
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading}
                    ref={submitButtonRef}
                  >
                    Login
                  </button>
                )}
              </form>
            </div>
          ) : (
            <div className="login-form">
              <div className="login-brand">
                <span className="login-brand-badge">
                  <FaLayerGroup />
                </span>
              </div>
              <h2>ERP</h2>
              <p>Reset Password</p>
              <form onSubmit={handleForgotPassword}>
                <div className="login-field">
                  <FaUser className="field-icon" />
                  <input
                    type="text"
                    placeholder="Enter your username"
                    value={forgotUsername}
                    onChange={(e) => setForgotUsername(e.target.value)}
                    onKeyDown={(e) => handleKeyDown(e, newPasswordRef)}
                  />
                </div>

                <div className="password-container">
                  <FaLock className="field-icon" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder="New Password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    ref={newPasswordRef}
                    onKeyDown={(e) => handleKeyDown(e, confirmPasswordRef)}
                  />
                  <div
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowNewPassword((prev) => !prev)}
                  >
                    {showNewPassword ? <FaEye /> : <FaEyeSlash />}
                  </div>
                </div>

                <div className="password-container">
                  <FaLock className="field-icon" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm New Password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    ref={confirmPasswordRef}
                    onKeyDown={(e) => handleKeyDown(e, null, true)}
                  />
                  <div
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <FaEye /> : <FaEyeSlash />}
                  </div>
                </div>

                <div className="login-error-message-container">
                  {forgotPasswordError && (
                    <div className="login-error-message">
                      {forgotPasswordError}
                    </div>
                  )}
                  {forgotPasswordSuccess && (
                    <div className="login-success-message">
                      {forgotPasswordSuccess}
                    </div>
                  )}
                </div>
                <div className="forgot-password-actions">
                  <a
                    href="#"
                    className="back-to-login"
                    onClick={toggleForgotPassword}
                  >
                    <FaArrowLeft /> Back to Login
                  </a>
                </div>
                {isLoading ? (
                  <div className="loader-container">
                    <span className="loader"></span>
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={isLoading}
                    ref={submitButtonRef}
                  >
                    Reset Password
                  </button>
                )}
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
